import { useCallback, useMemo, useRef } from 'react';
import { useRealtimeContext } from '@utils/providers/realtime-context.tsx';
import {
  Components as VirtuosoComponents,
  Virtuoso,
  VirtuosoHandle
} from 'react-virtuoso';
import { RealtimeEditorRow } from '@components/editors/realtime-editor/body/realtime-editor-row.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import cn from 'classnames';

export const RealtimeBody = () => {
  const { finals } = useRealtimeContext();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const { vars, cls } = useThemeVars();

  const getEditorValue = useMemo(() => {
    if (!finals || finals.length === 0) return [];
    return finals
      .filter(f => f.is_final)
      .map((final, index) => ({
        id: index,
        text: final.text,
        offset_ms: final.offset_ms,
        words: final.words || [],
        is_final: final.is_final,
        speaker: final.speaker || 'Speaker'
      }));
  }, [finals]);

  const renderRow = useCallback(
    (index: number) => {
      const segment = getEditorValue[index];
      return (
        <RealtimeEditorRow
          key={`realtime-row-${segment.offset_ms}-${index}`}
          index={index}
          segment={segment}
        />
      );
    },
    [getEditorValue]
  );

  const vComponents: VirtuosoComponents = useMemo(
    () => ({
      Item: ({ children, ...props }) => (
        <div {...props} style={{ padding: '0.5rem 1rem' }}>
          {children}
        </div>
      )
    }),
    []
  );

  if (getEditorValue.length === 0) {
    return (
      <div
        className={cn('p-8 text-center rounded-2xl', cls.cardBg)}
        style={vars}
      >
        <p className="text-gray-500 dark:text-zinc-400 italic">
          No final transcripts yet. Start speaking to see transcripts appear
          here.
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col h-full gap-4 min-h-full',
        cls.pageBg,
        cls.text
      )}
      style={vars}
    >
      <div className="flex flex-col h-full">
        <div
          className={cn(
            'flex-1 rounded-2xl text-lg shadow pb-4 overflow-auto min-h-72',
            'border',
            cls.cardBg,
            cls.border
          )}
        >
          <Virtuoso
            ref={virtuosoRef}
            totalCount={getEditorValue.length}
            style={{ height: '100%', width: '100%', overflowX: 'hidden' }}
            components={vComponents}
            itemContent={renderRow}
            // Cap initial synchronous mount — each row is a full Slate editor;
            // the whole length mounted every editor at once. (See caption-main.tsx.)
            initialItemCount={Math.min(getEditorValue.length, 20)}
            increaseViewportBy={200}
            followOutput="smooth"
          />
        </div>
      </div>
    </div>
  );
};
